import styled from "@emotion/styled";
import {
  fontSize,
  borderRadius,
  duration,
  layout,
  shadow,
  nav,
  text,
  delayVisibility,
  inAndOutOpacity,
} from "@atlas-int/tokens";

export { delayVisibility, inAndOutOpacity };

// ─── Legacy styled-components (kept for backward compat & internal use) ─────

export const OptionIcon = styled.div`
  width: 23px;
  height: 23px;
  text-align: center;
  padding: 5px;
  display: flex;
  justify-content: center;
  align-items: center;

  & img {
    margin: auto;
    width: 18px;
  }
`;

export const OptionLabel = styled.span`
  display: block;
  color: ${text.onDark};
  text-align: center;
  font-size: ${fontSize.sm};
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  animation-name: ${delayVisibility};
  animation-duration: ${duration.fast};
  animation-delay: ${duration.fast};
  animation-timing-function: linear;
  animation-iteration-count: 1;
  animation-fill-mode: forwards;
`;

export const OptionsListItem = styled.div`
  flex: 1 1;
  width: 33%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0px 9px;
  cursor: pointer;

  &.expanded {
    animation-name: ${inAndOutOpacity};
    animation-duration: 0.5s;
    animation-delay: 0s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  &.closed {
    animation-name: ${inAndOutOpacity};
    animation-duration: 0.5s;
    animation-delay: 0s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  &:hover > div {
    background-color: ${nav.surface};
    border-radius: ${borderRadius.lg};
  }
`;

export const disabledVizTooltipClassName = "disabledVizTooltip";
export const VizTypeList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0px;
  margin: 0px;

  & a {
    text-decoration: none;
    color: inherit;
  }
`;

export const OptionsList = styled.ul`
  display: flex;
  flex-direction: column;
  width: 100%;
  padding: 0px;
  margin-top: auto;

  & a {
    text-decoration: none;
    color: inherit;
  }
`;

export const OptionsListRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-around;
  margin-bottom: 9px;

  &.expanded {
    animation-name: ${inAndOutOpacity};
    animation-duration: 0.5s;
    animation-delay: 0s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  &.closed {
    animation-name: ${inAndOutOpacity};
    animation-duration: 0.5s;
    animation-delay: 0s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }
`;

export const MenuRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin: 4px 0px;
  width: calc(${layout.navBarExpandedWidth} - 45px);
  box-sizing: border-box;
  margin-right: 5px;

  &:hover {
    background-color: ${nav.surface};
  }

  &.selected {
    background-color: ${nav.surface};
    box-shadow: ${shadow.sm};
  }

  &.disabledVisOption {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &.disabledVisOption > * {
    display: flex;
    flex-direction: row;
    align-items: center;
    border: none;
  }

  &.disabledVisOption:hover .${disabledVizTooltipClassName} {
    visibility: visible;
  }
`;

export const MenuRowIcon = styled.div`
  width: ${layout.navBarWidth};
  flex: 0 0 auto;
  display: flex;
  justify-content: center;
`;

export const MenuHeaderGrowthLabIcon = styled.div`
  padding: 10px 0px 0px 0px;
  margin: 0px;
  display: flex;
  justify-content: center;
  width: ${layout.navBarWidth};
  overflow: hidden;

  & img {
    width: 45px;
    height: 45px;
  }

  &.expanded {
    width: 100%;
    justify-content: flex-start;
    padding-left: 10px;
  }

  &.expanded img {
    width: 180px;
    height: auto;
    padding-bottom: 8px;
    animation: ${inAndOutOpacity} ${duration.slow} linear forwards;
  }
`;

export const MenuRowLabel = styled.p`
  flex: 1 0;
  color: ${text.onDark};
  font-size: ${fontSize.sm};
  margin: 0px;
  padding: 4px 6px 4px 4px;

  & p {
    width: 100%;
  }
`;

export const OpenPanelArrowContainer = styled.div`
  width: 100%;
  display: flex;
  justify-content: flex-end;
  padding: 4px 8px;
  box-sizing: border-box;
`;

export const countryProfilesUrlBase = "/countries";

export const NotAvailableTooltip = styled.div`
  position: fixed;
  left: 70px;
  max-width: 220px;
  font-size: ${fontSize.sm};
  background-color: ${text.onDark};
  text-align: left;
  padding: 5px;
  box-sizing: border-box;
  z-index: 100;
  box-shadow: ${shadow.sm};
  margin-top: -10px;
  cursor: default;

  &:after {
    content: "";
    position: absolute;
    top: calc(50% - 10px);
    left: -10px;
    width: 0;
    height: 0;
    border-top: 10px solid transparent;
    border-bottom: 10px solid transparent;
    border-right: 10px solid ${text.onDark};
  }
`;

export const DisabledVizTypeTooltipContainer = styled.div`
  margin: 0px;
  padding: 0px;
  width: ${layout.navBarExpandedWidth};
`;

export const ExternalLinkImage = styled.img`
  width: 16px;
`;

// ─── Styled roots for compound components ───────────────────────────────────

export const NavOptionRoot = styled.button`
  flex: 1 1;
  width: 33%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  margin: 0px 9px;
  cursor: pointer;
  background: none;
  border: none;
  padding: 0;
  font: inherit;

  &.expanded {
    animation-name: ${inAndOutOpacity};
    animation-duration: 0.5s;
    animation-delay: 0s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  &.closed {
    animation-name: ${inAndOutOpacity};
    animation-duration: 0.5s;
    animation-delay: 0s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  &:hover > div {
    background-color: ${nav.surface};
    border-radius: ${borderRadius.lg};
  }
`;

export const NavBarRoot = styled.nav`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`;

export const NavGroupRoot = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: space-around;
  margin-bottom: 9px;

  &.expanded {
    animation-name: ${inAndOutOpacity};
    animation-duration: 0.5s;
    animation-delay: 0s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }

  &.closed {
    animation-name: ${inAndOutOpacity};
    animation-duration: 0.5s;
    animation-delay: 0s;
    animation-timing-function: linear;
    animation-iteration-count: 1;
    animation-fill-mode: forwards;
  }
`;

export const NavLinkRoot = styled.div`
  display: flex;
  flex-direction: row;
  align-items: flex-start;
  margin: 4px 0px;
  width: calc(${layout.navBarExpandedWidth} - 45px);
  box-sizing: border-box;
  margin-right: 5px;

  &:hover {
    background-color: ${nav.surface};
  }

  &.selected {
    background-color: ${nav.surface};
    box-shadow: ${shadow.sm};
  }

  &.disabledVisOption {
    cursor: not-allowed;
    opacity: 0.4;
  }

  &.disabledVisOption > * {
    display: flex;
    flex-direction: row;
    align-items: center;
    border: none;
  }

  &.disabledVisOption:hover .${disabledVizTooltipClassName} {
    visibility: visible;
  }
`;

export const NavItemRoot = styled.li`
  width: 100%;
  color: ${text.onDark};
  align-items: center;
  cursor: default;
  position: relative;
  background-color: transparent;

  & a,
  span {
    display: flex;
    padding: 5px 18px;
    text-transform: uppercase;
    letter-spacing: 0.08rem;
    font-size: ${fontSize["2xl"]};
    text-decoration: none;
    color: inherit;
    cursor: pointer;
  }
`;

export const NavFlyoutRoot = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  padding: 1rem;
  list-style-type: none;
`;

export const NavFlyoutItemRoot = styled.li`
  text-align: left;
  color: ${text.onDark};

  & a {
    text-transform: none;
    letter-spacing: 0px;
  }
`;

export const NavHamburgerRoot = styled.ul`
  display: flex;
  flex-direction: column;
  align-items: center;
  height: 100%;
  width: ${layout.navBarExpandedWidth};
  color: rgb(51, 51, 52);
  position: absolute;
  top: 0px;
  list-style-type: none;
  margin: 0px;
  padding: 0px;
  z-index: 1000;
  background-color: ${nav.background};
`;
